/** Application entry point: wire the pieces together once, then hand control to the router. */
import { applyTheme } from './app/theme.js';
import { installScene } from './app/scene.js';
import { installShell, loadNotifications } from './app/shell.js';
import { api } from './core/api.js';
import { applyServerFeatures, config } from './core/config.js';
import { reportError, setErrorReporter } from './core/errors.js';
import { installEvents } from './core/events.js';
import { defineRoutes, startRouter } from './core/router.js';
import { session } from './core/session.js';
import { allRoutes } from './features/index.js';
import { installForms } from './ui/forms.js';
import { installModal } from './ui/modal.js';
import { installToasts } from './ui/toast.js';

function watchConnection() {
  const strip = document.createElement('div');
  strip.className = 'offline';
  strip.setAttribute('role', 'status');
  strip.textContent = 'You are offline. Changes will not be saved until you reconnect.';
  strip.hidden = true;
  document.body.append(strip);
  const sync = () => { strip.hidden = navigator.onLine; };
  window.addEventListener('online', sync);
  window.addEventListener('offline', sync);
  sync();
}

function watchErrors() {
  window.addEventListener('error', (event) => reportError(event.error || new Error(event.message), { silent: true }));
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason?.name === 'AbortError') return;
    reportError(event.reason instanceof Error ? event.reason : new Error(String(event.reason)), { silent: true });
  });
}

async function boot() {
  applyTheme();
  installScene();
  watchErrors();
  watchConnection();
  installEvents();
  installModal();
  installToasts();
  installForms();
  installShell();
  defineRoutes(allRoutes);
  if (config.sentryDsn) setErrorReporter(() => { /* hook an error tracker here; see README */ });
  await session.restore();
  try { applyServerFeatures((await api.get('/public/info', { auth: false })).features); } catch { /* keep the wording from config.js */ }
  await startRouter();
  if (session.user) loadNotifications();
}

boot().catch((error) => {
  console.error(error);
  const app = document.getElementById('app');
  if (app) app.innerHTML = '<div style="padding:40px;font-family:system-ui"><h2>VConnect could not start</h2><p>Please reload the page. If it keeps happening, contact support.</p></div>';
});
